import { useLocation, useInRouterContext } from "react-router-dom";
import {
  useLocale,
  setLocaleCookie,
  getLocalizedPath,
  type Locale,
} from "@/shared/i18n/locale";
import styles from "./LanguageSelector.module.css";

interface LanguageSelectorProps {
  variant?: "dark" | "light";
  compact?: boolean;
  mobile?: boolean;
  onSelect?: () => void;
  className?: string;
}

export function LanguageSelector({
  variant = "dark",
  compact: _compact = false,
  mobile = false,
  onSelect,
  className,
}: LanguageSelectorProps) {
  const locale = useLocale();
  const inRouter = useInRouterContext();
  const location = inRouter ? useLocation() : null;

  const handleSelect = (targetLocale: Locale) => {
    if (targetLocale === locale) {
      onSelect?.();
      return;
    }

    setLocaleCookie(targetLocale);
    const pathname =
      location?.pathname ||
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const search =
      location?.search ||
      (typeof window !== "undefined" ? window.location.search : "");
    const hash =
      location?.hash ||
      (typeof window !== "undefined" ? window.location.hash : "");
    const targetPath = getLocalizedPath(pathname + search + hash, targetLocale);
    if (typeof window !== "undefined") {
      window.location.assign(targetLocale === "pt" ? targetPath.replace(/^\/(?=[?#]|$)/, "/pt/") : targetPath);
    }
    onSelect?.();
  };

  const isLight = variant === "light" || mobile;

  const containerClassName = [
    styles.container,
    isLight && styles.lightContainer,
    mobile && styles.mobileContainer,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const separatorClassName = [
    styles.separator,
    isLight && styles.lightSeparator,
  ]
    .filter(Boolean)
    .join(" ");

  const ptBtnClass = [
    styles.btn,
    isLight && styles.lightBtn,
    mobile && styles.mobileBtn,
    locale === "pt" && (isLight ? styles.lightActive : styles.active),
    locale === "pt" && mobile && styles.mobileActive,
  ]
    .filter(Boolean)
    .join(" ");

  const enBtnClass = [
    styles.btn,
    isLight && styles.lightBtn,
    mobile && styles.mobileBtn,
    locale === "en" && (isLight ? styles.lightActive : styles.active),
    locale === "en" && mobile && styles.mobileActive,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      role="group"
      aria-label={locale === "en" ? "Select language" : "Selecionar idioma"}
      className={containerClassName}
    >
      <button
        type="button"
        className={ptBtnClass}
        aria-label="Português"
        aria-pressed={locale === "pt"}
        aria-current={locale === "pt" ? "true" : undefined}
        onClick={() => handleSelect("pt")}
      >
        PT
      </button>
      <span className={separatorClassName} aria-hidden="true" />
      <button
        type="button"
        className={enBtnClass}
        aria-label="English"
        aria-pressed={locale === "en"}
        aria-current={locale === "en" ? "true" : undefined}
        onClick={() => handleSelect("en")}
      >
        EN
      </button>
    </div>
  );
}
